import _ from "lodash";
import {
  determinePaginationParams,
  determineSearchParams,
  determineSelector,
  getResource,
  readUrl,
  readUrlObjectBased,
  throwOnApiError,
  updateResource,
} from "./api";

const getAll = async (options = { page: 1, per_page: 10 }) => {
  const paginationParams = determinePaginationParams(options);
  const url = readUrl("pr_profiles", { paginationParams });
  return getResource(url);
};

// RLS drops any of these ids the viewer isn't entitled to see, so the returned id set is also the answer
// to "which of these profiles can this viewer open?". `select` narrows the returned columns for callers
// that only need that answer — the view is wide (AI bio + citations, tsv_content, contact_details json),
// so an entitlement probe should not drag all of it across. Omit it to get every column, as the display
// consumers do.
const getAllById = async (ids, options = { page: 1, per_page: 10 }) => {
  const idQueryString = `in.(${ids.join(",")})`;
  const { select, ...rest } = options;
  const url = readUrlObjectBased("pr_profiles", {
    conditionParams: { id: idQueryString, ...(select && { select }) },
    ...rest,
  });
  return getResource(url);
};

const getAllFullTextSearch = async (searchString, fieldsToSearch, options = { page: 1, per_page: 20 }) => {
  const searchParams = determineSearchParams({ searchString, fieldsToSearch, partialWordMatch: true });
  const paginationParams = determinePaginationParams({ ...options, returnType: "object" });
  const url = readUrlObjectBased("pr_profiles", { searchParams, paginationParams });
  return getResource(url);
};

const get = async (id) => {
  const selector = determineSelector(id, { keyId: "id", keySlug: "slug" });
  const url = readUrl("pr_profiles", { selector });
  return getResource(url);
};

// Name and bio live behind two endpoints (change_name_and_slug + the generic update), so this fires up
// to two sequential PATCHes. throwOnApiError makes the FIRST failing request throw immediately, so we
// never fire the second after a failed first (and never report a masked partial save — e.g. name fails
// but bio succeeds). The caller's try/catch keeps the modal open and surfaces the error.
const updateById = async (id, attributes) => {
  const baseUrl = `/id/api/user_profiles/${id}`;
  // eslint-disable-next-line camelcase
  const { first_name, last_name, ...rest } = attributes;

  let lastResult = { data: {} }; // the default result

  // Update name if either first_name or last_name were non-null
  // eslint-disable-next-line camelcase
  const nameAttributes = _.omitBy({ first_name, last_name }, _.isNil);
  if (!_.isEmpty(nameAttributes)) {
    const nameUrl = `${baseUrl}/change_name_and_slug`;
    lastResult = throwOnApiError(await updateResource(nameUrl, { data: { attributes: nameAttributes } }));
  }

  // Update the rest of the attributes using the generic endpoint
  if (!_.isEmpty(rest)) {
    lastResult = throwOnApiError(await updateResource(baseUrl, { data: { attributes: rest } }));
  }

  // Return the last result as it contains the most up-to-date state of affairs
  return lastResult;
};

export default { getAll, getAllById, getAllFullTextSearch, get, updateById };
